/** Multi-org helpers — a user can own a personal workspace and also belong to company orgs. */

export type UserOrg = {
  id: number;
  name: string;
  role: string;
  account_type?: string;
  logo_url?: string | null;
};

export function findCompanyOrg(orgs: UserOrg[] | undefined | null): UserOrg | null {
  if (!orgs?.length) return null;
  return orgs.find((o) => o.account_type === "company") ?? null;
}

export function findPersonalOrg(orgs: UserOrg[] | undefined | null): UserOrg | null {
  if (!orgs?.length) return null;
  return orgs.find((o) => (o.account_type || "individual") === "individual") ?? null;
}

/** Org pages / team features → company membership when present. */
export function pickCompanyOrg(orgs: UserOrg[] | undefined | null): UserOrg | null {
  return findCompanyOrg(orgs);
}

/** Billing / personal API keys / self-serve → personal workspace when present. */
export function pickPersonalOrg(orgs: UserOrg[] | undefined | null): UserOrg | null {
  return findPersonalOrg(orgs) ?? null;
}

/**
 * Context-aware pick:
 * - company → Acme-style org
 * - personal → individual workspace
 * - any → company first, else first membership
 */
export function pickOrg(
  orgs: UserOrg[] | undefined | null,
  prefer: "company" | "personal" | "any" = "any",
): UserOrg | null {
  if (!orgs?.length) return null;
  if (prefer === "company") return findCompanyOrg(orgs) ?? orgs[0] ?? null;
  if (prefer === "personal") return findPersonalOrg(orgs) ?? orgs[0] ?? null;
  return findCompanyOrg(orgs) ?? orgs[0] ?? null;
}

export function isOrgAdminRole(role: string | null | undefined): boolean {
  return role === "owner" || role === "admin";
}
